import { create } from "zustand";
import { MessageState, Message } from "./types";
import { HistoryMessage } from "src/services/api/chat";
import { useContextDocumentsStore } from "src/store/contextDocumentsStore";

export const useMessageStore = create<MessageState>()((set, get) => ({
    messages: [],
    actualContext: undefined,
    jsonContext: undefined,
    isStreaming: false,
    setActualContext: (newContext) => set({ actualContext: newContext }),
    setJsonContext: (newContext) => set({ jsonContext: newContext }),
    setIsStreaming: (value: boolean) => set({ isStreaming: value }),
    addMessage: (newMsg: Partial<Message>) => {
        const message: Message = {
            id: newMsg.id || `message-${Date.now()}-${Math.random()}`,
            text: newMsg.text || "",
            timestamp: newMsg.timestamp || new Date(),
            context: newMsg.context,
            answer: newMsg.answer,
            status: newMsg.status,
            streamStatus: newMsg.streamStatus,
            jsonMessage: newMsg.jsonMessage,
            questionText: newMsg.questionText,
            jsonContext: newMsg.jsonContext,
            attachments: newMsg.attachments,
        };
        set({ messages: [...get().messages, message] });
        return message.id;
    },
    updateMessage: (id: string, patch: Partial<Message>) => {
        set({
            messages: get().messages.map((m) => (m.id === id ? { ...m, ...patch } : m)),
        });
    },
    appendMessageText: (id: string, chunk: string) => {
        set({
            messages: get().messages.map((m) =>
                m.id === id ? { ...m, text: (m.text || "") + chunk } : m
            ),
        });
    },
    setMessages: (newMessages) => set({ messages: newMessages }),
    loadHistoryMessages: (historyMessages: HistoryMessage[]) => {
        const transformedMessages: Message[] = [];
        const baseTime = Date.now() - historyMessages.length * 2 * 60000;
        const contextDocuments: { filename: string; mimeType: string }[] = [];

        historyMessages.forEach((historyItem, index) => {
            const messageIndex = index * 2;
            const questionTime = new Date(baseTime + messageIndex * 60000);
            const answerTime = new Date(baseTime + (messageIndex + 1) * 60000);

            const contextString =
                typeof historyItem.context === "string" ? historyItem.context : undefined;
            const contextObject =
                typeof historyItem.context === "object" && historyItem.context !== null
                    ? historyItem.context
                    : undefined;

            if (historyItem.attachments?.length) {
                contextDocuments.push(
                    ...historyItem.attachments.map((a) => ({ filename: a.filename, mimeType: a.mime_type }))
                );
            }

            transformedMessages.push({
                id: `history-question-${index}-${Date.now()}`,
                text: historyItem.question,
                timestamp: questionTime,
                answer: false,
                status: "done",
                questionText: historyItem.question,
                attachments: historyItem.attachments?.map((a) => ({ filename: a.filename, mime_type: a.mime_type })),
            });

            const answerMessage: Message = {
                id: `history-answer-${index}-${Date.now()}`,
                text: historyItem.answer,
                timestamp: answerTime,
                answer: true,
                status: "done",
                questionText: historyItem.question,
            };

            if (contextString) {
                answerMessage.context = contextString;
            }

            if (contextObject) {
                answerMessage.jsonMessage = {
                    natural_language_response: historyItem.answer,
                    context: contextObject,
                };
            } else {
                answerMessage.jsonMessage = {
                    natural_language_response: historyItem.answer,
                };
            }

            transformedMessages.push(answerMessage);
        });

        set({ messages: transformedMessages });
        useContextDocumentsStore.getState().setDocuments(contextDocuments);
    },
}));
